import { useMemo } from 'react'
import { filterStoreRepo } from 'components/Filters/FilterStore'
import { MobileFilterGroup } from 'components/Filters/components/FilterModal'
import {
  SelectFilter,
  SelectFilterOption,
} from 'components/Filters/fields/SelectFilter'
import { FilterChips } from 'components/Filters/components/FilterChips/FilterChips'
import { FilterIdentifier } from 'models/saved_view'
import useApiService from 'services/api'

export function MobileCompanySearchFilterset({
  filterIdentifier,
}: {
  filterIdentifier: FilterIdentifier
}) {
  const api = useApiService()
  const { data: categoriesData } = api.useGetActiveCompanyCategoriesAndTypes()

  // disablePersist must match the hook's call site (useMobileSearchFilterStore):
  // the URL is the source of truth for this store, and getStore only honors the
  // flag on first creation, so both sites pass true or the store persists.
  const filterStore = filterStoreRepo.getStore(
    filterIdentifier,
    /* disablePersist */ true
  )
  const { pendingValueFilters } = filterStore()

  const categoryOptions: SelectFilterOption[] = useMemo(() => {
    if (!categoriesData) return []
    return [...categoriesData]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((category) => ({ label: category.name, value: category.slug }))
  }, [categoriesData])

  // Type options cascade from the selected categories: with no category
  // selected we show every sub-category; otherwise only the sub-categories
  // belonging to the selected categories. "Other" sub-categories get a
  // category prefix to disambiguate, matching the previous dropdown.
  const typeOptions: SelectFilterOption[] = useMemo(() => {
    if (!categoriesData) return []

    const selectedCategorySlugs = new Set(
      (pendingValueFilters?.['category'] ?? []).map((f) => f.value)
    )

    const seen = new Set<string>()
    const options: SelectFilterOption[] = []
    categoriesData.forEach((category) => {
      if (
        selectedCategorySlugs.size > 0 &&
        !selectedCategorySlugs.has(category.slug)
      ) {
        return
      }
      category.sub_categories.forEach((subCategory) => {
        if (seen.has(subCategory.slug)) return
        seen.add(subCategory.slug)
        options.push({
          label:
            subCategory.name === 'Other'
              ? `${category.name} - Other`
              : subCategory.name,
          value: subCategory.slug,
        })
      })
    })

    return options.sort((a, b) => a.label.localeCompare(b.label))
  }, [categoriesData, pendingValueFilters])

  return (
    <>
      <MobileFilterGroup title="Company Details">
        <SelectFilter
          filterKey="category"
          title="Opp Category"
          options={categoryOptions}
          filterStore={filterStore}
        />
        <SelectFilter
          filterKey="type"
          title="Type"
          options={typeOptions}
          filterStore={filterStore}
        />
      </MobileFilterGroup>

      <FilterChips
        identifier={filterIdentifier}
        store={filterStore}
        className="ml-0"
        clearAllButton
      />
    </>
  )
}
